import { useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Download,
  FileSpreadsheet,
  FileUp,
  Fuel,
  Gauge,
  MoreHorizontal,
  Paperclip,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { extractErrorMessage } from '@/lib/api-client';
import { formatDate } from '@/lib/format';
import { getUploadDownloadUrl } from '@/features/documents/api';
import {
  centsToNumber,
  exportToXlsx,
  isoToBrDate,
} from '@/lib/excel-export';
import { useDeleteFueling, useFuelingList, useFuelingSummary, useImportFueling } from './api';
import { centsToReais, FUEL_TYPE_LABELS, mlPerKmToKmPerL, mlToLiters, type FuelingRecord } from './types';
import { FuelingFormDialog } from './fueling-form-dialog';

export function FuelingPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FuelingRecord | null>(null);
  const [_importResult, setImportResult] = useState<{ imported: number; errors: Array<{ row: number; error: string }> } | null>(null);

  const listQuery = useFuelingList({ page: 1, limit: 50, sort: '-fueledAt' });
  const summaryQuery = useFuelingSummary();
  const deleteMutation = useDeleteFueling();
  const importMutation = useImportFueling();

  const records = listQuery.data?.data ?? [];
  const summary = summaryQuery.data?.data;

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Abastecimento removido');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleOpenReceipt = async (uploadId: string) => {
    try {
      const url = await getUploadDownloadUrl(uploadId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleExportXlsx = () => {
    if (records.length === 0) {
      toast.error('Nenhum registro para exportar.');
      return;
    }
    const rows = records.map((r) => ({
      Data: isoToBrDate(r.fueledAt),
      Veículo: r.vehicle?.plate ?? '',
      Marca: r.vehicle?.brand ?? '',
      Modelo: r.vehicle?.model ?? '',
      Combustível: FUEL_TYPE_LABELS[r.fuelType] ?? r.fuelType,
      Litros: mlToLiters(r.quantityMl),
      'R$/Litro': centsToNumber(r.priceCentsPerL),
      'Valor Total': centsToNumber(r.totalCents),
      'km/L': r.consumptionMlPerKm ? mlPerKmToKmPerL(r.consumptionMlPerKm) : '',
      'Tanque cheio': r.isFull ? 'Sim' : 'Não',
      Anomalia: r.isAnomaly ? `Sim — ${r.anomalyReason ?? ''}` : 'Não',
      Observações: r.notes ?? '',
    }));
    exportToXlsx(rows, {
      filename: 'frota-abastecimentos',
      sheetName: 'Abastecimentos',
      columnWidths: [12, 10, 12, 16, 14, 10, 10, 14, 8, 12, 28, 30],
    });
    toast.success(`${records.length} registros exportados`);
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        try {
          const csvRecords = (result.data as Record<string, string>[]).map((row) => ({
            plate: row['placa'] || row['plate'] || '',
            fuelType: row['combustivel'] || row['fuelType'] || 'flex',
            quantityL: parseFloat(row['litros'] || row['quantityL'] || '0'),
            pricePerL: parseFloat(row['preco_litro'] || row['pricePerL'] || '0'),
            totalReais: parseFloat(row['total'] || row['totalReais'] || '0'),
            odometerKm: row['odometro'] || row['odometerKm'] ? parseFloat(row['odometro'] || row['odometerKm'] || '0') : undefined,
            fueledAt: row['data'] || row['fueledAt'] || new Date().toISOString(),
            stationName: row['posto'] || row['stationName'] || undefined,
            notes: row['observacao'] || row['notes'] || undefined,
          }));
          const result2 = await importMutation.mutateAsync(csvRecords);
          setImportResult(result2);
          toast.success(`${result2.imported} registros importados`);
        } catch (err) {
          toast.error(extractErrorMessage(err));
        }
      },
      error: (err) => {
        toast.error(`Erro ao ler CSV: ${err.message}`);
      },
    });
    e.target.value = '';
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Fuel className="size-3.5" /> Operação
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Abastecimento</h1>
          <p className="text-sm text-muted-foreground">
            Lançamentos, consumo e anomalias da sua frota.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportXlsx}
            disabled={records.length === 0}
          >
            <FileSpreadsheet className="size-4" /> Excel
          </Button>
          <label>
            <input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
            <Button variant="outline" asChild>
              <span><FileUp className="size-4" /> Importar CSV</span>
            </Button>
          </label>
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="size-4" /> Novo
          </Button>
        </div>
      </header>

      <Tabs defaultValue="records">
        <TabsList>
          <TabsTrigger value="records"><Fuel className="size-4" /> Lançamentos ({records.length})</TabsTrigger>
          <TabsTrigger value="dashboard"><BarChart3 className="size-4" /> Dashboard</TabsTrigger>
        </TabsList>

        {/* Tab: Lançamentos */}
        <TabsContent value="records">
          <Card>
            <CardContent className="p-0">
              {listQuery.isLoading ? (
                <div className="space-y-3 p-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
              ) : records.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <Fuel className="size-10 text-muted-foreground" />
                  <p className="text-sm font-medium">Nenhum abastecimento registrado</p>
                  <p className="text-xs text-muted-foreground">Comece registrando manualmente ou importe um CSV.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3 font-medium">Data</th>
                        <th className="px-4 py-3 font-medium">Veículo</th>
                        <th className="px-4 py-3 font-medium">Combustível</th>
                        <th className="px-4 py-3 font-medium text-right">Litros</th>
                        <th className="px-4 py-3 font-medium text-right">R$/L</th>
                        <th className="px-4 py-3 font-medium text-right">Total</th>
                        <th className="px-4 py-3 font-medium text-right">km/L</th>
                        <th className="px-4 py-3 font-medium text-center">Cupom</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="w-10 px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {records.map((r) => {
                        const kmPerL = r.consumptionMlPerKm ? mlPerKmToKmPerL(r.consumptionMlPerKm) : null;
                        return (
                          <tr key={r.id} className="transition-colors hover:bg-muted/30">
                            <td className="px-4 py-3 text-xs">{formatDate(r.fueledAt)}</td>
                            <td className="px-4 py-3">
                              <span className="font-mono text-xs font-semibold">
                                {r.vehicle?.plate ?? '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs">{FUEL_TYPE_LABELS[r.fuelType] ?? r.fuelType}</td>
                            <td className="px-4 py-3 text-right font-mono text-xs tabular-nums">
                              {mlToLiters(r.quantityMl).toFixed(1)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs tabular-nums">
                              {(r.priceCentsPerL / 100).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs font-medium tabular-nums">
                              {centsToReais(r.totalCents)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs tabular-nums">
                              {kmPerL ? kmPerL.toFixed(1) : '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {r.receiptUploadId ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenReceipt(r.receiptUploadId!)}
                                  className="inline-flex size-7 items-center justify-center rounded-md text-success transition-colors hover:bg-success/10"
                                  title="Ver comprovante"
                                >
                                  <Paperclip className="size-4" />
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {r.isAnomaly ? (
                                <Badge variant="destructive" className="gap-1">
                                  <AlertTriangle className="size-3" /> Anomalia
                                </Badge>
                              ) : (
                                <Badge variant="success">OK</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="size-8">
                                    <MoreHorizontal className="size-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {r.receiptUploadId && (
                                    <DropdownMenuItem onClick={() => handleOpenReceipt(r.receiptUploadId!)}>
                                      <Download className="size-4" /> Baixar comprovante
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => setDeleteTarget(r)} className="text-destructive">
                                    <Trash2 className="size-4" /> Remover
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Dashboard */}
        <TabsContent value="dashboard">
          {summaryQuery.isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
            </div>
          ) : summary ? (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                  title="Total gasto"
                  value={centsToReais(summary.totalCostCents)}
                  icon={<Fuel className="size-5" />}
                  color="text-primary bg-primary/10"
                />
                <KpiCard
                  title="Litros abastecidos"
                  value={`${summary.totalLiters.toFixed(0)} L`}
                  icon={<Gauge className="size-5" />}
                  color="text-success bg-success/10"
                />
                <KpiCard
                  title="Média km/L"
                  value={summary.avgConsumptionKmPerL ? `${summary.avgConsumptionKmPerL.toFixed(1)} km/L` : '—'}
                  icon={<TrendingUp className="size-5" />}
                  color="text-warning bg-warning/10"
                />
                <KpiCard
                  title="Anomalias"
                  value={String(summary.anomalyCount)}
                  icon={<AlertTriangle className="size-5" />}
                  color={summary.anomalyCount > 0 ? 'text-destructive bg-destructive/10' : 'text-muted-foreground bg-muted'}
                />
              </div>

              {/* Gráfico mensal */}
              {summary.byMonth.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Custo mensal de combustível</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={summary.byMonth.map((m) => ({
                        ...m,
                        custoR: m.totalCostCents / 100,
                        litros: m.totalLiters,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                        <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                        <Tooltip
                          contentStyle={{
                            background: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            fontSize: '12px',
                          }}
                          formatter={(value, name) =>
                            name === 'custoR'
                              ? [`R$ ${Number(value).toFixed(2)}`, 'Custo']
                              : [`${Number(value).toFixed(0)} L`, 'Litros']
                          }
                        />
                        <Legend />
                        <Bar dataKey="custoR" name="Custo (R$)" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="litros" name="Litros" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Custo por veículo */}
              {summary.byVehicle.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Custo por veículo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summary.byVehicle.map((v) => (
                        <div key={v.vehicleId} className="flex items-center justify-between rounded-lg border border-border p-3">
                          <div>
                            <span className="font-mono text-sm font-semibold">{v.plate}</span>
                            <p className="text-xs text-muted-foreground">
                              {v.totalLiters.toFixed(0)} L · {v.avgKmPerL ? `${v.avgKmPerL.toFixed(1)} km/L` : '—'}
                            </p>
                          </div>
                          <span className="font-mono text-sm font-medium">{centsToReais(v.totalCostCents)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Sem dados suficientes para exibir o dashboard.</p>
          )}
        </TabsContent>
      </Tabs>

      <FuelingFormDialog open={formOpen} onOpenChange={setFormOpen} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover abastecimento</DialogTitle>
            <DialogDescription>Tem certeza?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} loading={deleteMutation.isPending}>Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ title, value, icon, color }: { title: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-5">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</p>
          <p className="font-mono text-2xl font-semibold tabular-nums">{value}</p>
        </div>
        <div className={`flex size-10 items-center justify-center rounded-lg ${color}`}>{icon}</div>
      </CardContent>
    </Card>
  );
}
